<template>
  <div class="ra-hotspots" :data-refapp-tagging-id="taggingId" :style="rootStyles">
    <refapp-component-content
      :caption="caption"
      :title="title"
      :heading-level="headingLevel"
      :description="description"
      :cta-text="cta?.label"
      :cta-href="cta?.url"
      container-type="page"
      :cta-attrs="{
        'data-refapp-tagging-target': 'main-cta',
      }"
    />

    <div ref="container" class="ra-hotspots__wrapper">
      <picture class="ra-hotspots__picture">
        <source media="(min-width: 768px)" :srcset="image.md" />
        <img
          :src="imageSrc"
          :alt="imageAlt"
          @load="handleImageLoad"
          @error="handleImageLoadError"
        />
      </picture>
      <ul v-if="isImageFirstLoadResolved && pois.length" class="ra-hotspots__pois">
        <refapp-hotspots-poi
          v-for="(poi, index) in currentScreenPois"
          :key="index"
          :poi="poi"
          :current-poi-position-key="currentPoiPositionKey"
          :is-active="activePoiId === poi.id"
          :container-ref="$refs.container"
          :container-size="containerSize"
          :image-natural-size="imageNaturalSize"
          :screen-size-key="screenSizeKey"
          @openTooltip="openTooltip(poi.id)"
          @closeTooltip="closeTooltip(poi.id, { returnFocusToMarker: $event.returnFocusToMarker })"
        />
      </ul>
    </div>
  </div>
</template>

<script>
import { debounce } from '../../../shared/utils/debounce';
import { SCREEN_SIZE } from '../constants';
import RefappComponentContent from '../../../shared/components/component-content/refapp-component-content.vue';
import RefappHotspotsPoi from './refapp-hotspots-poi.vue';

export default {
  name: 'refapp-hotspots',
  components: {
    RefappComponentContent,
    RefappHotspotsPoi,
  },
  inject: [
    'caption',
    'title',
    'headingLevel',
    'description',
    'cta',
    'image',
    'imageAlt',
    'pois',
    'taggingId',
    'markerBorderColor',
    'markerFillColor',
  ],
  data() {
    return {
      activePoiId: null,
      containerSize: { width: 0, height: 0 },
      imageNaturalSize: { width: 0, height: 0 },
      isImageFirstLoadResolved: false,
      resizeObserver: null,
      windowWidth: null,
    };
  },
  computed: {
    screenSizeKey() {
      if (this.windowWidth > 767) {
        return SCREEN_SIZE.lg;
      }
      return SCREEN_SIZE.sm;
    },
    imageSrc() {
      return this.image.sm || this.image.md;
    },
    currentPoiPositionKey() {
      if (!this.image.sm) {
        return SCREEN_SIZE.lg;
      }
      return this.screenSizeKey;
    },
    currentScreenPois() {
      return this.pois.filter((poi) => {
        const { x, y } = poi.position[this.currentPoiPositionKey] || {};

        return !!x && !!y;
      });
    },
    rootStyles() {
      return {
        // fallback values are set as @property in vars file
        '--hotspot-marker-bg-color': this.markerFillColor || undefined,
        '--hotspot-marker-border-color': this.markerBorderColor || undefined,
      };
    },
  },
  watch: {
    screenSizeKey() {
      this.closeTooltip(this.activePoiId);
    },
  },
  created() {
    this.handleWindowResize();
    window.addEventListener('resize', this.handleWindowResize);
  },
  mounted() {
    this.updateContainerSize();
    this.initResizeObserver();
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    this.beforeUnmount();
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.handleWindowResize);
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleWindowResize() {
      this.windowWidth = window.innerWidth;
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.closeTooltip(this.activePoiId);
      }
    },
    handleImageLoad(event) {
      const img = event.target;
      this.imageNaturalSize = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
      this.isImageFirstLoadResolved = true;
      this.updateContainerSize();
    },
    handleImageLoadError() {
      this.isImageFirstLoadResolved = true;
    },
    initResizeObserver() {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      if (this.$refs.container) {
        this.resizeObserver.observe(this.$refs.container);
      }
    },
    handleResize: debounce(function () {
      this.updateContainerSize();
    }, 100),
    updateContainerSize() {
      if (!this.$refs.container) return;

      const { width, height } = this.$refs.container.getBoundingClientRect();
      this.containerSize = { width, height };
    },
    openTooltip(poiId) {
      if (this.activePoiId !== poiId) {
        this.activePoiId = poiId;
      }
    },
    closeTooltip(poiId, { returnFocusToMarker = true } = {}) {
      if (!this.activePoiId || poiId !== this.activePoiId) return;

      this.activePoiId = null;

      if (returnFocusToMarker) {
        this.returnFocusToMarker(poiId);
      }
    },
    returnFocusToMarker(poiId) {
      setTimeout(() => {
        if (this.activePoiId) return;
        const marker = this.$refs.container?.querySelector(
          `.js-ra-hotspots-poi-marker[data-poi-id="${poiId}"]`,
        );
        marker?.focus({ preventScroll: true });
      });
    },
  },
};
</script>

<style lang="scss">
@use '../styles/style.scss';
</style>
